// Shimmer skeletons for the KCC screens. Each composed skeleton reuses the real
// page container classes (.detail__panel, .tenants__card, .detail__grid2, …) so
// the loading state mirrors the final layout instead of a bare spinner.

import React from "react";
import "./style.css";

type Size = number | string;

interface ShimmerProps {
	w?: Size;
	h?: Size;
	r?: Size;
	className?: string;
	style?: React.CSSProperties;
}

// A single shimmering placeholder block.
export const Shimmer: React.FC<ShimmerProps> = ({ className = "", h = 12, r = 6, style, w = "100%" }) => (
	<span className={`kcc-skel ${className}`} style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const keys = (n: number) => Array.from({ length: n }, (_, i) => i);

// Generic tinted-header table skeleton. `wideFirst` renders the first column as a
// two-line (title + subtitle) cell — matches the name/entity columns.
export const SkeletonTable: React.FC<{ cols: number; rows?: number; wideFirst?: boolean }> = ({
	cols,
	rows = 6,
	wideFirst = true,
}) => (
	<div className="kcc-skel-table">
		<div className="kcc-skel-thead">
			{keys(cols).map((i) => (
				<div key={i} className={`kcc-skel-cell ${wideFirst && i === 0 ? "kcc-skel-cell--wide" : ""}`}>
					<Shimmer w={i === 0 ? "50%" : "42%"} h={11} />
				</div>
			))}
		</div>
		{keys(rows).map((r) => (
			<div key={r} className="kcc-skel-tr">
				{keys(cols).map((i) =>
					wideFirst && i === 0 ? (
						<div key={i} className="kcc-skel-cell kcc-skel-cell--wide">
							<div className="kcc-skel-stack">
								<Shimmer w="65%" h={12} />
								<Shimmer w="40%" h={9} />
							</div>
						</div>
					) : (
						<div key={i} className="kcc-skel-cell">
							<Shimmer w={`${45 + ((r + i) % 3) * 14}%`} h={12} />
						</div>
					),
				)}
			</div>
		))}
	</div>
);

// ── List pages ────────────────────────────────────────────────────────────
export const TenantsSkeleton: React.FC = () => (
	<div className="tenants">
		<div className="tenants__pagehead">
			<Shimmer w={140} h={26} r={8} />
			<div className="tenants__pagehead-actions">
				<Shimmer w={110} h={38} r={24} />
				<Shimmer w={150} h={38} r={24} />
			</div>
		</div>
		<div className="tenants__toolbar">
			<div className="tenants__pills">
				{keys(5).map((i) => (
					<Shimmer key={i} w={82} h={32} r={16} />
				))}
			</div>
			<Shimmer w="28rem" h={40} r={10} />
			<div className="kcc-skel-row" style={{ marginLeft: "auto" }}>
				{keys(4).map((i) => (
					<Shimmer key={i} w={90} h={30} r={16} />
				))}
			</div>
		</div>
		<div className="tenants__card">
			<SkeletonTable cols={8} rows={6} />
		</div>
	</div>
);

export const AccessSkeleton: React.FC = () => (
	<div className="access">
		<div className="access__header">
			<div className="access__heading">
				<Shimmer w={90} h={24} r={8} />
				<Shimmer w={300} h={26} r={999} />
			</div>
		</div>
		<div className="access__filterbar">
			<Shimmer w="24rem" h={40} r={10} />
			<Shimmer w={150} h={38} r={24} />
		</div>
		<div className="access__card">
			<SkeletonTable cols={4} rows={6} />
		</div>
	</div>
);

export const AuditSkeleton: React.FC = () => (
	<div className="audit">
		<div className="audit__header">
			<div className="audit__heading">
				<Shimmer w={130} h={24} r={8} />
				<Shimmer w={340} h={26} r={999} />
			</div>
		</div>
		<div className="audit__filterbar">
			<Shimmer w="28rem" h={40} r={10} />
			<Shimmer w={160} h={38} r={24} />
		</div>
		<div className="audit__table">
			<SkeletonTable cols={5} rows={8} />
		</div>
	</div>
);

// Releases (Deployment plan / Dev timeline) — tab row + a board-shaped block.
export const ReleasesSkeleton: React.FC = () => (
	<div className="releases">
		<div style={{ display: "flex", gap: "1.6rem", alignItems: "center", marginBottom: "1rem" }}>
			<Shimmer w={140} h={22} r={8} />
			<Shimmer w={110} h={22} r={8} />
			<span style={{ marginLeft: "auto" }}>
				<Shimmer w={130} h={36} r={10} />
			</span>
		</div>
		<SkeletonTable cols={6} rows={8} />
	</div>
);

// ── Tenant-detail tabs ──────────────────────────────────────────────────────
export const OverviewSkeleton: React.FC = () => (
	<div className="detail__grid2">
		<div className="detail__panel">
			<div className="detail__panel-hd">
				<Shimmer w={140} h={14} />
			</div>
			{keys(7).map((i) => (
				<div key={i} className="kcc-skel-kv">
					<Shimmer w={110} h={12} />
					<Shimmer w={150} h={12} />
				</div>
			))}
		</div>
		<div className="detail__panel">
			<div className="detail__panel-hd">
				<Shimmer w={210} h={14} />
			</div>
			<SkeletonTable cols={3} rows={4} wideFirst={false} />
		</div>
	</div>
);

export const BrandingSkeleton: React.FC = () => (
	<div className="detail__brand-grid">
		<div className="detail__panel">
			<div className="detail__panel-hd">
				<Shimmer w={120} h={14} />
			</div>
			{keys(4).map((i) => (
				<div key={i} className="kcc-skel-kv">
					<Shimmer w={120} h={12} />
					<Shimmer w={i > 1 ? 44 : 90} h={i > 1 ? 44 : 12} r={i > 1 ? 8 : 6} />
				</div>
			))}
			<div className="kcc-skel-swatches">
				{keys(5).map((i) => (
					<div key={i} className="kcc-skel-swatch">
						<Shimmer w={48} h={42} r={11} />
						<Shimmer w={40} h={9} />
					</div>
				))}
			</div>
			<Shimmer w="100%" h={72} r={9} style={{ marginTop: "1.5rem" }} />
		</div>
		<div className="detail__panel">
			<div className="detail__panel-hd">
				<Shimmer w={120} h={14} />
			</div>
			<Shimmer w="100%" h={460} r={10} />
		</div>
	</div>
);

export const CostSkeleton: React.FC = () => (
	<div className="detail__cost">
		<div className="detail__panel">
			<div className="detail__panel-hd">
				<Shimmer w={180} h={14} />
			</div>
			<Shimmer w="100%" h={300} r={10} />
			<div style={{ marginTop: "1.4rem" }}>
				<SkeletonTable cols={7} rows={6} wideFirst={false} />
			</div>
		</div>
	</div>
);

// Whole detail page while the tenant list resolves on a deep link / refresh
// (breadcrumb + sub-tabs + the Overview body).
export const TenantDetailSkeleton: React.FC = () => (
	<div className="detail">
		<div className="detail__head">
			<div className="kcc-skel-row">
				<Shimmer w={64} h={14} />
				<Shimmer w={12} h={14} />
				<Shimmer w={80} h={14} />
			</div>
		</div>
		<div className="detail__subtabs">
			<div className="detail__subtab-list">
				{keys(4).map((i) => (
					<Shimmer key={i} w={72} h={16} style={{ margin: "0.7rem 0" }} />
				))}
			</div>
		</div>
		<OverviewSkeleton />
	</div>
);

export const GovernanceSkeleton: React.FC = () => (
	<div className="detail__gov">
		<div className="detail__gov-head">
			<div>
				<Shimmer w={190} h={20} r={8} />
				<Shimmer w={360} h={12} style={{ marginTop: "0.6rem" }} />
			</div>
			<Shimmer w={140} h={40} r={24} />
		</div>
		<div className="detail__gov-total">
			<div className="detail__gov-total-info">
				<Shimmer w={38} h={38} r={10} />
				<div className="kcc-skel-stack">
					<Shimmer w={130} h={13} />
					<Shimmer w={80} h={10} />
				</div>
			</div>
			<Shimmer w={60} h={14} />
		</div>
		<Shimmer w={70} h={14} style={{ margin: "0 0 0.9rem" }} />
		<div className="detail__plan-grid">
			{keys(4).map((i) => (
				<div key={i} className="detail__plan-card">
					<div className="detail__plan-card-top">
						<Shimmer w={110} h={16} />
						<Shimmer w={20} h={20} r={6} />
					</div>
					<div className="kcc-skel-row" style={{ gap: "1.5rem" }}>
						<Shimmer w={70} h={26} />
						<Shimmer w={70} h={26} />
					</div>
					<Shimmer w="80%" h={11} style={{ marginTop: "auto" }} />
				</div>
			))}
		</div>
	</div>
);
